import React, { useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  Stack,
  FormLabel,
  Box,
  InputGroup,
  Input,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
} from "@chakra-ui/react";
import { ExpertPriceCard } from "../List/ExpertPriceCard";
import ExpertDataService from "../../services/Expert";
import { PayoutPriceTables } from "../Expert/PayoutPriceTables";
import {
  emptyPayoutAudiences,
  normalizePayoutSheet,
  toStoredPayoutSheet,
} from "../../Config/ExpertPayoutPrices";

const ExpertPriceDrawer = ({ isOpen, onClose, data, id }: any) => {
  const firstField: any = React.useRef();
  const [isEditable, setIsEditable] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [priceId, setPriceId] = useState("");
  const [newData, setNewData] = useState(normalizePayoutSheet({}));
  const [list, setList] = useState(data || []);
  const [addDate, setAddDate] = useState({
    fromDate: "",
    toDate: "",
  });
  const [addPrices, setAddPrices] = useState(emptyPayoutAudiences());

  const closeDrawer = () => {
    setPriceId("");
    onClose();
  };

  const handleSwitchTab = (index: number) => {
    setActiveTab(index);
  };

  const editFunction = (nextPriceId: any, priceData: any) => {
    setPriceId(nextPriceId);
    setNewData(normalizePayoutSheet(priceData));
    handleSwitchTab(2);
  };

  const updatePrice = () => {
    if (isEditable) {
      ExpertDataService.updatePricesToExpert(
        id,
        priceId,
        toStoredPayoutSheet(newData)
      ).then((res: any) => {
        setList(res.data.data.prices);
      });
    }
    setIsEditable(!isEditable);
  };

  const deleteFunction = (nextPriceId: any) => {
    ExpertDataService.deletePricesToExpert(id, nextPriceId).then((res: any) => {
      setList(res.data.data.prices);
    });
  };

  const addNewPrices = () => {
    const submittedData = toStoredPayoutSheet({
      fromDate: addDate.fromDate,
      toDate: addDate.toDate,
      ...addPrices,
    });
    ExpertDataService.addPricesToExpert(id, submittedData)
      .then((res: any) => {
        setList(res.data.data.prices);
        handleSwitchTab(1);
        setAddPrices(emptyPayoutAudiences());
        setAddDate({ fromDate: "", toDate: "" });
      })
      .catch((e) => {
        console.log(e);
        window.alert("Something went wrong");
      });
  };

  const handleAddPriceChange = (
    audience: string,
    mode: string,
    sessionKey: string,
    fieldValue: string
  ) => {
    setAddPrices((prev: any) => ({
      ...prev,
      [audience]: {
        ...prev[audience],
        [mode]: {
          ...prev[audience][mode],
          [sessionKey]: fieldValue,
        },
      },
    }));
  };

  const handleEditPriceChange = (
    audience: string,
    mode: string,
    sessionKey: string,
    fieldValue: string
  ) => {
    setNewData((prev: any) => ({
      ...prev,
      [audience]: {
        ...prev[audience],
        [mode]: {
          ...prev[audience]?.[mode],
          [sessionKey]: fieldValue,
        },
      },
    }));
  };

  return (
    <div>
      <Drawer
        isOpen={isOpen}
        placement="right"
        initialFocusRef={firstField}
        onClose={closeDrawer}
        size={"xl"}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerBody>
            <Tabs
              isFitted
              variant="enclosed"
              index={activeTab}
              defaultIndex={2}
              onChange={handleSwitchTab}
            >
              <TabList mb="1em">
                <Tab>Add new price</Tab>
                <Tab>Price List</Tab>
                <Tab>Edit Price</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <Stack spacing="24px">
                    <Flex justifyContent={"space-evenly"}>
                      <Box width="48%">
                        <FormLabel htmlFor="From">From</FormLabel>
                        <InputGroup>
                          <Input
                            type="date"
                            id="From"
                            value={addDate.fromDate}
                            onChange={(e) =>
                              setAddDate({
                                ...addDate,
                                fromDate: e.target.value,
                              })
                            }
                          />
                        </InputGroup>
                      </Box>
                      <Box width="48%">
                        <FormLabel htmlFor="To">To</FormLabel>
                        <InputGroup>
                          <Input
                            type="date"
                            id="To"
                            value={addDate.toDate}
                            onChange={(e) =>
                              setAddDate({
                                ...addDate,
                                toDate: e.target.value,
                              })
                            }
                          />
                        </InputGroup>
                      </Box>
                    </Flex>
                    <p className="text-muted mb-0">
                      Expert payout prices are per audience type for each
                      delivery mode.
                    </p>
                    <PayoutPriceTables
                      value={addPrices}
                      onChange={handleAddPriceChange}
                    />
                    <Button colorScheme="blue" onClick={() => addNewPrices()}>
                      Submit
                    </Button>
                  </Stack>
                </TabPanel>
                <TabPanel>
                  {list.length > 0 &&
                    list.map((el: any) => (
                      <ExpertPriceCard
                        key={el.id}
                        props={el}
                        editFunction={editFunction}
                        deleteFunction={deleteFunction}
                      />
                    ))}
                </TabPanel>
                <TabPanel>
                  {priceId !== "" && (
                    <Stack spacing="24px">
                      <Flex align="center">
                        <Flex flex="1" />
                        <Button
                          variant="outline"
                          onClick={() => {
                            updatePrice();
                          }}
                        >
                          {isEditable ? "Save" : "Edit"}
                        </Button>
                      </Flex>
                      <Flex justifyContent="space-evenly">
                        <Box width="48%">
                          <FormLabel htmlFor="from">From</FormLabel>
                          <InputGroup>
                            <Input
                              type="date"
                              id="from"
                              size="lg"
                              value={newData.fromDate}
                              onChange={(e) =>
                                setNewData({
                                  ...newData,
                                  fromDate: e.target.value,
                                })
                              }
                              disabled={!isEditable}
                            />
                          </InputGroup>
                        </Box>
                        <Box width="48%">
                          <FormLabel htmlFor="to">To</FormLabel>
                          <InputGroup>
                            <Input
                              type="date"
                              id="to"
                              size="lg"
                              value={newData.toDate}
                              onChange={(e) =>
                                setNewData({
                                  ...newData,
                                  toDate: e.target.value,
                                })
                              }
                              disabled={!isEditable}
                            />
                          </InputGroup>
                        </Box>
                      </Flex>
                      <PayoutPriceTables
                        value={newData}
                        onChange={handleEditPriceChange}
                        disabled={!isEditable}
                      />
                    </Stack>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default ExpertPriceDrawer;